import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import {
  GeneralPermissions,
  SettingsPermissions,
} from "@redotech/redo-model/permissions";
import { SettingsSectionEnum } from "@redotech/redo-web/arbiter-components/navigation/redo-settings-nav";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { Navigate } from "react-router-dom";
import {
  aiSettingsRoutes,
  inAiContextLibraryView,
  inAiKnowledgeView,
} from "../../../setting/ai-agent/route";
import { conversionRoutes } from "../../../setting/conversion/route";
import { isNewFlowEditorEnabled } from "../../../setting/flow-editor/use-flow-editor-preference";
import { inFaqBuilder, inFaqHomeView } from "./faq-routes";

const attributeDefinitionsBreadcrumb = breadcrumbSlot();

const FaqRedirect = () => {
  return <Navigate replace to="/support/faq/home" />;
};

const fullscreen = (url: string) => {
  return url.includes("ad-blocks/") && !url.endsWith("ad-blocks/");
};

const inNewFlowEditor = (url: string) => {
  const isFlowEditorPath =
    url.includes("/flows/returns") ||
    url.includes("/flows/claims") ||
    url.includes("/flows/warranties");

  return isFlowEditorPath && isNewFlowEditorEnabled();
};

const inLocationEditor = (url: string) => {
  return url.includes("location-flow-editor");
};

const inLabelsEditor = (url: string) => {
  return url.includes("labels-flow-editor");
};

const inForge = (url: string) => {
  return (
    (url.includes("reviews-displays-widgets/") &&
      !url.endsWith("reviews-displays-widgets/")) ||
    (url.includes("upsell-page/") && !url.endsWith("upsell-page/")) ||
    (url.includes("oms/automations") && !url.endsWith("oms/automations")) ||
    inFaqBuilder(url) ||
    url.includes("oms/warehouse-builder") ||
    url.includes("conversion/general/checkout-widgets-builder") ||
    inWorkOrderFlowBuilder(url)
  );
};

const inPhoneSetup = (url: string) => {
  return url.includes("phone-setup");
};

const inBilling = (url: string) => {
  return url.includes("/settings/billing");
};

const inAutomationsList = (url: string) => {
  return url.endsWith("oms/automations");
};

const inWorkOrderFlowsList = (url: string) => {
  return url.endsWith("oms/work-order-flows");
};

const inWorkOrderFlowBuilder = (url: string) => {
  return url.includes("oms/work-order-flows/");
};

const isWarehouseLayout = (url: string) => {
  return url.endsWith("oms/warehouse-layouts");
};

const isFullwidthTable = (url: string) => {
  return url.includes("settings/attributes");
};

function hidePadding(url: string) {
  return (
    inAiKnowledgeView(url) ||
    inAiContextLibraryView(url) ||
    inAutomationsList(url) ||
    inWorkOrderFlowsList(url) ||
    fullscreen(url) ||
    inForge(url) ||
    inPhoneSetup(url) ||
    inBilling(url) ||
    isWarehouseLayout(url) ||
    inFaqHomeView(url) ||
    isFullwidthTable(url) ||
    url.includes("customer-portal")
  );
}

// Settings routes organized by section
const settingsRoutesBySection: Record<
  SettingsSectionEnum,
  PermissionedRouteObject[] | PermissionedRouteObject
> = {
  [SettingsSectionEnum.GENERAL]: [
    {
      handle: {
        breadcrumb: "Billing",
        permission: SettingsPermissions.SET_GENERAL_SETTINGS,
      },
      path: "billing",
      children: [
        {
          index: true,
          lazy: async () => {
            const { BillingPage } = await import(
              "../../../setting/billing/billing"
            );
            return { Component: BillingPage };
          },
        },
        {
          handle: {
            breadcrumb: "Outbound",
            permission: SettingsPermissions.SET_GENERAL_SETTINGS,
          },
          path: "outbound",
          children: [
            {
              lazy: async () => {
                const { OutboundBillingDetail } = await import(
                  "../../../setting/billing/outbound-billing-detail"
                );
                return { Component: OutboundBillingDetail };
              },
              children: [
                { index: true, element: <Navigate replace to="general" /> },
                {
                  path: "general",
                  lazy: async () => {
                    const { BalanceTransactionTable } = await import(
                      "../../../setting/billing/outbound-labels-ledger/balance-transaction-table"
                    );
                    return { Component: BalanceTransactionTable };
                  },
                },
                {
                  path: "balance",
                  lazy: async () => {
                    const { OutboundLabelBalancePage } = await import(
                      "../../../setting/outbound-label-balance"
                    );
                    return { Component: OutboundLabelBalancePage };
                  },
                },
                {
                  path: "disputes",
                  lazy: async () => {
                    const { SurchargeDisputeTable } = await import(
                      "../../../setting/billing/surcharge-disputes/surcharge-dispute-table"
                    );
                    return { Component: SurchargeDisputeTable };
                  },
                },
              ],
            },
            {
              async lazy() {
                const { InvoiceDetailPageWrapper, breadcrumb } = await import(
                  "../../../setting/billing/outbound-labels-ledger/invoice-detail-page-wrapper"
                );
                return {
                  Component: InvoiceDetailPageWrapper,
                  handle: {
                    breadcrumb,
                    permission: SettingsPermissions.SET_GENERAL_SETTINGS,
                  },
                };
              },
              path: "outbound-invoice/:invoiceId",
            },
          ],
        },
        {
          handle: {
            breadcrumb: "Returns",
            permission: SettingsPermissions.SET_GENERAL_SETTINGS,
          },
          path: "returns",
          children: [
            {
              lazy: async () => {
                const { ReturnsBillingDetail } = await import(
                  "../../../setting/billing/returns-billing-detail"
                );
                return { Component: ReturnsBillingDetail };
              },
              children: [
                {
                  index: true,
                  lazy: async () => {
                    const { ReturnsBillingIndexRedirect } = await import(
                      "../../../setting/billing/returns-billing-detail"
                    );
                    return { Component: ReturnsBillingIndexRedirect };
                  },
                },
                {
                  path: "general",
                  lazy: async () => {
                    const { ReturnsBilling } = await import(
                      "../../../setting/billing/returns-billing"
                    );
                    return { Component: ReturnsBilling };
                  },
                },
                {
                  path: "invoices",
                  lazy: async () => {
                    const { Payouts } = await import(
                      "@redotech/redo-merchant-general-app/billing/payouts"
                    );
                    return { Component: Payouts };
                  },
                },
              ],
            },
          ],
        },
        {
          handle: {
            breadcrumb: "Inventory management",
            permission: SettingsPermissions.SET_GENERAL_SETTINGS,
          },
          path: "ims",
          children: [
            {
              lazy: async () => {
                const { ImsBillingDetail } = await import(
                  "../../../setting/billing/ims-billing-detail"
                );
                return { Component: ImsBillingDetail };
              },
              children: [
                {
                  index: true,
                  element: <Navigate replace to="subscription" />,
                },
                {
                  path: "subscription",
                  lazy: async () => {
                    const { ImsBilling } = await import(
                      "../../../setting/billing/ims-billing"
                    );
                    return { Component: ImsBilling };
                  },
                },
                {
                  path: "forecasting",
                  lazy: async () => {
                    const { ImsForecastingBilling } = await import(
                      "../../../setting/billing/ims-forecasting-billing"
                    );
                    return { Component: ImsForecastingBilling };
                  },
                },
              ],
            },
          ],
        },
        {
          path: ":productSlug",
          lazy: async () => {
            const { BillingProductDetail, productBreadcrumb } = await import(
              "../../../setting/billing/billing-product-detail"
            );
            return {
              Component: BillingProductDetail,
              handle: {
                breadcrumb: productBreadcrumb,
                permission: SettingsPermissions.SET_GENERAL_SETTINGS,
              },
            };
          },
        },
      ],
    },
    {
      handle: {
        breadcrumb: "Brand kit",
        permission: SettingsPermissions.SET_GENERAL_SETTINGS,
      },
      path: "brand-kit",
      lazy: async () => {
        const { BrandKitPage } = await import(
          "@redotech/redo-merchant-general-app/setting/brand-kit/brand-kit"
        );
        return { Component: BrandKitPage };
      },
    },
    {
      handle: {
        breadcrumb: "Communication",
        permission: SettingsPermissions.SET_GENERAL_SETTINGS,
      },
      path: "communication",
      lazy: async () => {
        const { CommunicationPage } = await import(
          "@redotech/redo-merchant-general-app/setting/communication/communication"
        );
        return { Component: CommunicationPage };
      },
    },
    {
      handle: {
        breadcrumb: "Customer Portal",
        permission: SettingsPermissions.SET_GENERAL_SETTINGS,
      },
      path: "customer-portal",
      lazy: async () => {
        const { CustomerPortalPage: CustomerPortal } = await import(
          "@redotech/redo-merchant-general-app/setting/customer-portal/customer-portal"
        );
        return { Component: CustomerPortal };
      },
    },
    // We moved /return-portal -> /customer-portal, so we'll redirect users just in case.
    {
      path: "return-portal",
      element: <Navigate replace to="../customer-portal" />,
    },
    {
      handle: {
        breadcrumb: "Customer widget",
        permission: [
          SettingsPermissions.SET_GENERAL_SETTINGS,
          SettingsPermissions.SET_AI_SETTINGS,
          SettingsPermissions.SET_CUSTOMER_ACCOUNTS_SETTINGS,
          SettingsPermissions.SET_SUPPORT_SETTINGS,
        ],
      },
      path: "customer-widget",
      lazy: async () => {
        const { CustomerWidgetSettingsPage } = await import(
          "../../../setting/customer-widget/customer-widget-settings"
        );
        return { Component: CustomerWidgetSettingsPage };
      },
    },
    {
      handle: {
        breadcrumb: "Developer",
        permission: GeneralPermissions.MANAGE_TEAM_DEVELOPMENT,
      },
      path: "developer",
      lazy: async () => {
        const { DeveloperPage } = await import(
          "@redotech/redo-merchant-general-app/setting/developer"
        );
        return { Component: DeveloperPage };
      },
    },
    {
      path: "invoices",
      element: <Navigate replace to="../billing/returns/invoices" />,
    },
    {
      handle: {
        breadcrumb: "Localization",
        permission: SettingsPermissions.SET_GENERAL_SETTINGS,
      },
      path: "localization",
      lazy: async () => {
        const { LocalizationPage } = await import(
          "@redotech/redo-merchant-general-app/setting/localization/localization"
        );
        return { Component: LocalizationPage };
      },
    },
    {
      handle: {
        breadcrumb: "Notifications",
        permission: SettingsPermissions.SET_GENERAL_SETTINGS,
      },
      path: "notifications",
      lazy: async () => {
        const { NotificationsPage } = await import(
          "@redotech/redo-merchant-general-app/setting/notification/notifications"
        );
        return { Component: NotificationsPage };
      },
    },
    {
      handle: {
        breadcrumb: "Users",
        permission: GeneralPermissions.MANAGE_TEAM_USERS,
      },
      path: "users",
      children: [
        {
          path: "",
          lazy: async () => {
            const { UsersPage } = await import(
              "@redotech/redo-merchant-general-app/setting/user/user"
            );
            return { Component: UsersPage };
          },
        },
        {
          handle: { breadcrumb: "User information" },
          path: ":userId",
          lazy: async () => {
            const { ProfilePage } = await import(
              "@redotech/redo-merchant-general-app/setting/user/profile-page"
            );
            return { Component: ProfilePage };
          },
        },
      ],
    },
    {
      handle: {
        breadcrumb: "Roles",
        permission: GeneralPermissions.MANAGE_TEAM_ROLES,
      },
      path: "roles",
      lazy: async () => {
        const { RolesSettingsPage } = await import(
          "@redotech/redo-merchant-general-app/setting/roles/roles-settings"
        );
        return { Component: RolesSettingsPage };
      },
    },
  ],

  [SettingsSectionEnum.PERSONAL]: [
    {
      handle: { breadcrumb: "Personal" },
      path: "personal",
      children: [
        { index: true, element: <Navigate replace to="profile" /> },
        {
          handle: { breadcrumb: "Profile settings" },
          path: "profile",
          lazy: async () => {
            const { ProfilePage } = await import(
              "@redotech/redo-merchant-general-app/setting/user/profile-page"
            );
            return { Component: ProfilePage };
          },
        },
      ],
    },
  ],
  [SettingsSectionEnum.AI]: aiSettingsRoutes,
  [SettingsSectionEnum.CONVERSION]: conversionRoutes,
  [SettingsSectionEnum.CUSTOMER_ACCOUNTS]: [
    {
      handle: {
        breadcrumb: "Ad blocks",
        permission: SettingsPermissions.SET_CUSTOMER_ACCOUNTS_SETTINGS,
      },
      path: "customer-accounts-ad-blocks",
      children: [
        {
          index: true,
          lazy: async () => {
            const { AdBlocksPage } = await import(
              "../../../setting/customer-accounts/ad-blocks/ad-blocks-drawer"
            );
            return { Component: AdBlocksPage };
          },
        },
        {
          path: ":adBlockId",
          handle: {
            breadcrumb: "Edit Ad Block",
            permission: SettingsPermissions.SET_CUSTOMER_ACCOUNTS_SETTINGS,
          },
          lazy: async () => {
            const { AdBlockBuilder } = await import(
              "../../../setting/customer-accounts/ad-blocks/ad-blocks-builder"
            );
            return { element: <AdBlockBuilder /> };
          },
        },
      ],
    },
    {
      handle: {
        breadcrumb: "Integrations",
        permission: SettingsPermissions.SET_CUSTOMER_ACCOUNTS_SETTINGS,
      },
      path: "customer-accounts-integrations",
      lazy: async () => {
        const { IntegrationsPage } = await import(
          "../../../setting/customer-accounts/integrations/integrations"
        );
        return { Component: IntegrationsPage };
      },
    },
    {
      handle: {
        breadcrumb: "Storefront display",
        permission: SettingsPermissions.SET_CUSTOMER_ACCOUNTS_SETTINGS,
      },
      path: "customer-accounts-storefront-display",
      lazy: async () => {
        const { StorefrontDisplaysPage } = await import(
          "../../../setting/customer-accounts/storefront-displays"
        );
        return { Component: StorefrontDisplaysPage };
      },
    },
  ],

  [SettingsSectionEnum.MARKETING]: [
    {
      handle: {
        breadcrumb: "Audience",
        permission: SettingsPermissions.SET_MARKETING_SETTINGS,
      },
      path: "audience",
      lazy: async () => {
        const { AudienceSettingsPage } = await import(
          "../../../setting/marketing/audience-settings"
        );
        return { Component: AudienceSettingsPage };
      },
    },
    {
      handle: {
        breadcrumb: "Email Domain",
        permission: SettingsPermissions.SET_MARKETING_SETTINGS,
      },
      path: "email-domain",
      lazy: async () => {
        const { EmailDomainPage } = await import(
          "../../../setting/email-domain"
        );
        return { Component: EmailDomainPage };
      },
    },
    {
      handle: {
        breadcrumb: "SMS Marketing",
        permission: SettingsPermissions.SET_MARKETING_SETTINGS,
      },
      path: "sms-marketing",
      lazy: async () => {
        const { SmsSettingsPage } = await import(
          "../../../setting/marketing/sms-settings"
        );
        return { Component: SmsSettingsPage };
      },
    },
    {
      handle: {
        breadcrumb: "Keywords",
        permission: SettingsPermissions.SET_MARKETING_SETTINGS,
      },
      path: "subscribe-keywords",
      lazy: async () => {
        const { SubscribeKeywordsTable } = await import(
          "../../../setting/marketing/subscribe-keywords/subscribe-keywords"
        );
        return { Component: SubscribeKeywordsTable };
      },
    },
    {
      handle: {
        breadcrumb: "Integrations",
        permission: SettingsPermissions.SET_MARKETING_SETTINGS,
      },
      path: "marketing-integrations",
      children: [
        {
          index: true,
          lazy: async () => {
            const { IntegrationsPage } = await import(
              "../../../setting/marketing/marketing-integrations"
            );
            return { Component: IntegrationsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Orders API Documentation",
            permission: SettingsPermissions.SET_MARKETING_SETTINGS,
          },
          path: "orders-api-documentation",
          lazy: async () => {
            const { OrdersApiDocumentation } = await import(
              "../../../setting/marketing/integrations/orders-api-documentation"
            );
            return { Component: OrdersApiDocumentation };
          },
        },
      ],
    },
  ],

  [SettingsSectionEnum.ORDER_TRACKING]: [
    {
      handle: {
        breadcrumb: "General",
        permission: SettingsPermissions.SET_MARKETING_SETTINGS,
      },
      path: "order-tracking",
      lazy: async () => {
        const { OrderTrackingSettings } = await import(
          "../../../setting/order-tracking/order-tracking-settings"
        );
        return { Component: OrderTrackingSettings };
      },
    },
    {
      handle: {
        breadcrumb: "Integrations",
        permission: SettingsPermissions.SET_MARKETING_SETTINGS,
      },
      path: "order-tracking-integrations",
      lazy: async () => {
        const { OrderTrackingIntegrationsPage } = await import(
          "../../../setting/order-tracking/order-tracking-integrations"
        );
        return { Component: OrderTrackingIntegrationsPage };
      },
    },
  ],

  //   [SettingsSectionEnum.CATALOG]: [
  //     {
  //       handle: {
  //         breadcrumb: "Integrations",
  //         permission: GeneralPermissions.OTHER,
  //       },
  //       path: "catalog-integrations",
  //       lazy: async () => {
  //         const { CatalogIntegrationsPage } = await import(
  //           "../../../setting/catalog/integrations/catalog-integrations"
  //         );
  //         return { Component: CatalogIntegrationsPage };
  //       },
  //     },
  //   ],

  [SettingsSectionEnum.PRODUCTS]: [
    {
      handle: {
        breadcrumb: "Products",
        permission: SettingsPermissions.SET_GENERAL_SETTINGS,
      },
      path: "products",
      async lazy() {
        const { ProductPage } = await import(
          "../../../setting/product/product"
        );
        return { Component: ProductPage };
      },
    },
    {
      handle: {
        breadcrumb: "Attribute Definitions",
        permission: SettingsPermissions.SET_GENERAL_SETTINGS,
      },
      path: "attributes",
      children: [
        {
          index: true,
          lazy: async () => {
            const { AttributeDefinitionsLandingPage } = await import(
              "../../../setting/attribute-definitions"
            );
            return { Component: AttributeDefinitionsLandingPage };
          },
        },
        {
          handle: {
            breadcrumb: attributeDefinitionsBreadcrumb,
            permission: SettingsPermissions.SET_GENERAL_SETTINGS,
          },
          path: ":ownerType",
          async lazy() {
            const { AttributeDefinitionsPage } = await import(
              "../../../setting/attribute-definitions"
            );
            return {
              element: (
                <AttributeDefinitionsPage
                  breadcrumb={attributeDefinitionsBreadcrumb}
                />
              ),
            };
          },
        },
      ],
    },
  ],

  [SettingsSectionEnum.RETURNS_AND_CLAIMS]: [
    {
      handle: {
        breadcrumb: "Assessments",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "assessments",
      lazy: async () => {
        const { AssessmentsWithTabsPage } = await import(
          "../../../setting/assessments/assessments-with-tabs"
        );
        return { Component: AssessmentsWithTabsPage };
      },
    },
    {
      handle: {
        breadcrumb: "Automations",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "automations",
      lazy: async () => {
        const { AutomationsPage } = await import("../../../setting/automation");
        return { Component: AutomationsPage };
      },
    },
    {
      handle: {
        breadcrumb: "Bundles",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "bundles",
      lazy: async () => {
        const { BundlesPage } = await import(
          "../../../setting/bundles/bundles"
        );
        return { Component: BundlesPage };
      },
    },
    {
      handle: {
        breadcrumb: "Flows",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "flows",
      children: [
        {
          path: "",
          lazy: async () => {
            const { FlowsList } = await import(
              "../../../setting/flows/flows-list"
            );
            return { element: <FlowsList /> };
          },
        },
        {
          handle: {
            breadcrumb: "Returns",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "returns",
          lazy: async () => {
            const { ReturnsFlowEditor } = await import(
              "../../../setting/flows/returns-flow-editor"
            );
            return { element: <ReturnsFlowEditor /> };
          },
        },
        {
          handle: {
            breadcrumb: "AB test analytics",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "ab-test-analytics",
          lazy: async () => {
            const { ReturnFlowABTestAnalytics } = await import(
              "../../../setting/return-flow/ab-test-analytics"
            );
            return { element: <ReturnFlowABTestAnalytics /> };
          },
        },
        {
          handle: {
            breadcrumb: "Returns-finalize",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "returns-finalize",
          lazy: async () => {
            const { ReturnsFinalizeFlowEditor } = await import(
              "../../../setting/flows/returns-finalize-flow-editor"
            );
            return { element: <ReturnsFinalizeFlowEditor /> };
          },
        },
        {
          handle: {
            breadcrumb: "Claims",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "claims",
          lazy: async () => {
            const { ClaimsFlowEditor } = await import(
              "../../../setting/flows/claims-flow-editor"
            );
            return { element: <ClaimsFlowEditor /> };
          },
        },
        {
          handle: {
            breadcrumb: "Claims-finalize",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "claims-finalize",
          lazy: async () => {
            const { ClaimsFinalizeFlowEditor } = await import(
              "../../../setting/flows/claims-finalize-flow-editor"
            );
            return { element: <ClaimsFinalizeFlowEditor /> };
          },
        },
        {
          handle: {
            breadcrumb: "Warranties",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "warranties",
          lazy: async () => {
            const { WarrantiesFlowEditor } = await import(
              "../../../setting/flows/warranties-flow-editor"
            );
            return { element: <WarrantiesFlowEditor /> };
          },
        },
        {
          handle: {
            breadcrumb: "Warranties-registration",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "warranties-registration",
          lazy: async () => {
            const { WarrantiesRegistrationFlowEditor } = await import(
              "../../../setting/flows/warranties-registration-flow-editor"
            );
            return { element: <WarrantiesRegistrationFlowEditor /> };
          },
        },
      ],
    },
    {
      handle: {
        breadcrumb: "Coverage",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "coverage",
      lazy: async () => {
        const { CoveragePage: CoverageSettings } = await import(
          "../../../setting/coverage/coverage"
        );
        return { Component: CoverageSettings };
      },
    },
    {
      handle: {
        breadcrumb: "Exchanges",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "exchanges",
      lazy: async () => {
        const { ExchangesPage: Exchanges } = await import(
          "../../../setting/exchanges"
        );
        return { Component: Exchanges };
      },
    },
    {
      handle: {
        breadcrumb: "Info Modal",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "info-modal",
      lazy: async () => {
        const { InfoModalSettingsPage } = await import(
          "../../../setting/info-modal/info-modal-settings"
        );
        return { element: <InfoModalSettingsPage /> };
      },
    },
    {
      handle: {
        breadcrumb: "Integrations",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "integrations",
      children: [
        {
          path: "",
          lazy: async () => {
            const { IntegrationsPage } = await import(
              "../../../setting/integrations"
            );
            return { Component: IntegrationsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Comment Sold",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "comment-sold",
          lazy: async () => {
            const { CommentSoldPage } = await import(
              "../../../setting/comment-sold"
            );
            return { Component: CommentSoldPage };
          },
        },
        {
          handle: {
            breadcrumb: "NetSuite",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "netsuite",
          lazy: async () => {
            const { NetSuiteIntegrationSettingsPage } = await import(
              "../../../setting/netsuite-integration-settings"
            );
            return { Component: NetSuiteIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "ShipMonk",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "shipmonk",
          lazy: async () => {
            const { ShipmonkIntegrationSettingsPage } = await import(
              "../../../setting/shipmonk-integration-settings"
            );
            return { Component: ShipmonkIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Trackstar",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "trackstar",
          lazy: async () => {
            const { TrackstarIntegrationSettingsPage } = await import(
              "../../../setting/trackstar-integration-settings"
            );
            return { Component: TrackstarIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Aims360",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "aims360",
          lazy: async () => {
            const { Aims360IntegrationSettingsPage } = await import(
              "../../../setting/aims360-integration-settings"
            );
            return { Component: Aims360IntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "ApparelMagic",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "apparel-magic",
          lazy: async () => {
            const { ApparelMagicIntegrationSettingsPage } = await import(
              "../../../setting/apparel-magic-integration-settings"
            );
            return { Component: ApparelMagicIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Authorize.net",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "authorizenet",
          lazy: async () => {
            const { AuthorizeNetIntegrationSettingsPage } = await import(
              "../../../setting/authorizenet-integration-settings"
            );
            return { Component: AuthorizeNetIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Blue Yonder",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "blue-yonder",
          lazy: async () => {
            const { BlueYonderIntegrationSettingsPage } = await import(
              "../../../setting/blue-yonder-integration-settings"
            );
            return { Component: BlueYonderIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Brightpearl",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "brightpearl",
          lazy: async () => {
            const { BrightpearlIntegrationSettingsPage } = await import(
              "../../../setting/brightpearl-integration-settings"
            );
            return { Component: BrightpearlIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "CloudX",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "cloudx",
          lazy: async () => {
            const { CloudXIntegrationSettingsPage } = await import(
              "../../../setting/cloudx-integration-settings"
            );
            return { Component: CloudXIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Comment Sold",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "comment-sold/manage",
          lazy: async () => {
            const { CommentSoldManageIntegrationSettingsPage } = await import(
              "../../../setting/comment-sold-manage-integration-settings"
            );
            return { Component: CommentSoldManageIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "CreditsYard",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "creditsyard",
          lazy: async () => {
            const { CreditsYardIntegrationSettingsPage } = await import(
              "../../../setting/creditsyard-integration-settings"
            );
            return { Component: CreditsYardIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Custom Return",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "custom-return",
          lazy: async () => {
            const { CustomReturnIntegrationSettingsPage } = await import(
              "../../../setting/custom-return-integration-settings"
            );
            return { Component: CustomReturnIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Flexport",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "flexport",
          lazy: async () => {
            const { FlexportIntegrationSettingsPage } = await import(
              "../../../setting/flexport-integration-settings"
            );
            return { Component: FlexportIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Global-e",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "globale",
          lazy: async () => {
            const { GlobalEIntegrationSettingsPage } = await import(
              "../../../setting/globale-integration-settings"
            );
            return { Component: GlobalEIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "IDF Shipping",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "idf-shipping",
          lazy: async () => {
            const { IDFShippingIntegrationSettingsPage } = await import(
              "../../../setting/idf-shipping-integration-settings"
            );
            return { Component: IDFShippingIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Inmar",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "inmar",
          lazy: async () => {
            const { InmarIntegrationSettingsPage } = await import(
              "../../../setting/inmar-integration-settings"
            );
            return { Component: InmarIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Katana",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "katana",
          lazy: async () => {
            const { KatanaIntegrationSettingsPage } = await import(
              "../../../setting/katana-integration-settings"
            );
            return { Component: KatanaIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Klaviyo",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "klaviyo",
          lazy: async () => {
            const { KlaviyoIntegrationSettingsPage } = await import(
              "../../../setting/klaviyo-integration-settings"
            );
            return { Component: KlaviyoIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Loop",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "loop",
          lazy: async () => {
            const { LoopIntegrationSettingsPage } = await import(
              "../../../setting/loop-integration-settings"
            );
            return { Component: LoopIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Luminous",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "luminous",
          lazy: async () => {
            const { LuminousIntegrationSettingsPage } = await import(
              "../../../setting/luminous-integration-settings"
            );
            return { Component: LuminousIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Purple Dot",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "purple-dot",
          lazy: async () => {
            const { PurpleDotIntegrationSettingsPage } = await import(
              "../../../setting/purple-dot-integration-settings"
            );
            return { Component: PurpleDotIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Refundid",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "refundid",
          lazy: async () => {
            const { RefundidIntegrationSettingsPage } = await import(
              "../../../setting/refundid-integration-settings"
            );
            return { Component: RefundidIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "ReturnBear",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "returnbear",
          lazy: async () => {
            const { ReturnBearIntegrationSettingsPage } = await import(
              "../../../setting/returnbear-integration-settings"
            );
            return { Component: ReturnBearIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Rise.ai",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "rise-ai",
          lazy: async () => {
            const { RiseAiIntegrationSettingsPage } = await import(
              "../../../setting/rise-ai-integration-settings"
            );
            return { Component: RiseAiIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Rivo",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "rivo",
          lazy: async () => {
            const { RivoIntegrationSettingsPage } = await import(
              "../../../setting/rivo-integration-settings"
            );
            return { Component: RivoIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Seko",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "seko",
          lazy: async () => {
            const { SekoIntegrationSettingsPage } = await import(
              "../../../setting/seko-integration-settings"
            );
            return { Component: SekoIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "ShipBob",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "shipbob",
          lazy: async () => {
            const { ShipbobIntegrationSettingsPage } = await import(
              "../../../setting/shipbob-integration-settings"
            );
            return { Component: ShipbobIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "ShipEdge",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "shipedge",
          lazy: async () => {
            const { ShipedgeIntegrationSettingsPage } = await import(
              "../../../setting/shipedge-integration-settings"
            );
            return { Component: ShipedgeIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "ShipHero",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "shiphero",
          lazy: async () => {
            const { ShipheroIntegrationSettingsPage } = await import(
              "../../../setting/shiphero-integration-settings"
            );
            return { Component: ShipheroIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Teamwork Commerce",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "teamwork-commerce",
          lazy: async () => {
            const { TeamworkCommerceIntegrationSettingsPage } = await import(
              "../../../setting/teamwork-commerce-integration-settings"
            );
            return { Component: TeamworkCommerceIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "TikTok Shop",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "tiktok-shop",
          lazy: async () => {
            const { TiktokShopIntegrationSettingsPage } = await import(
              "../../../setting/tiktok-shop-integration-settings"
            );
            return { Component: TiktokShopIntegrationSettingsPage };
          },
        },
      ],
    },
    {
      handle: {
        breadcrumb: "Permissions",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "permissions",
      lazy: async () => {
        const { PermissionsPage } = await import(
          "../../../setting/permissions/permissions"
        );
        return { Component: PermissionsPage };
      },
    },
    {
      handle: {
        breadcrumb: "Point of sale",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "pos",
      lazy: async () => {
        const { PointOfSalePage } = await import(
          "../../../setting/point-of-sale"
        );
        return { Component: PointOfSalePage };
      },
    },
    {
      handle: {
        breadcrumb: "Processing",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "processing",
      lazy: async () => {
        const { ProcessingPage } = await import("../../../setting/processing");
        return { Component: ProcessingPage };
      },
    },
    {
      handle: {
        breadcrumb: "Returns UI",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "returns-ui",
      lazy: async () => {
        const { ReturnsUiPage } = await import(
          "../../../setting/returns-ui/returns-ui"
        );
        return { element: <ReturnsUiPage /> };
      },
    },
    {
      handle: {
        breadcrumb: "Shipping",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "shipping",
      children: [
        {
          path: "",
          lazy: async () => {
            const { ShippingPage } = await import(
              "../../../setting/shipping/shipping-page"
            );
            return { Component: ShippingPage };
          },
        },
        {
          handle: {
            breadcrumb: "Location flow",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "location-flow-editor",
          lazy: async () => {
            const { LocationFlowEditor } = await import(
              "../../../setting/flows/location-flow-editor"
            );
            return { Component: LocationFlowEditor };
          },
        },
        {
          handle: {
            breadcrumb: "Label flow",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "labels-flow-editor",
          lazy: async () => {
            const { LabelsFlowEditor } = await import(
              "../../../setting/flows/labels-flow-editor"
            );
            return { Component: LabelsFlowEditor };
          },
        },
      ],
    },
  ],

  [SettingsSectionEnum.WARRANTIES]: [
    {
      handle: {
        breadcrumb: "Registration Rewards",
        permission: SettingsPermissions.SET_WARRANTY_SETTINGS,
      },
      path: "registration-rewards",
      lazy: async () => {
        const { RegistrationRewards } = await import(
          "../../../setting/registration-rewards"
        );
        return { element: <RegistrationRewards /> };
      },
    },
  ],

  [SettingsSectionEnum.INVENTORY_MANAGEMENT]: [
    {
      handle: {
        breadcrumb: "Purchase orders",
        permission: GeneralPermissions.OTHER,
      },
      path: "inventory-management/purchase-orders",
      lazy: async () => {
        const { PurchaseOrderSettingsPage } = await import(
          "../../../setting/inventory-management/purchase-order-settings"
        );
        return { Component: PurchaseOrderSettingsPage };
      },
    },
    {
      handle: {
        breadcrumb: "QuickBooks Online",
        permission: GeneralPermissions.OTHER,
      },
      path: "inventory-management/quickbooks",
      children: [
        {
          index: true,
          lazy: async () => {
            const { QuickBooksIntegrationSettingsPage } = await import(
              "../../../setting/inventory-management/quickbooks-integration-settings"
            );
            return { Component: QuickBooksIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "QuickBooks account mappings",
            permission: GeneralPermissions.OTHER,
          },
          path: "account-mapping",
          lazy: async () => {
            const { QuickBooksAccountMappingPage } = await import(
              "../../../setting/inventory-management/quickbooks-account-mapping"
            );
            return { Component: QuickBooksAccountMappingPage };
          },
        },
      ],
    },
  ],

  [SettingsSectionEnum.ORDER_FULFILLMENT]: [
    {
      handle: {
        breadcrumb: "Order management",
        breadcrumbNavigate: false,
        permission: SettingsPermissions.SET_GENERAL_SETTINGS,
      },
      path: "order-management",
      children: [
        {
          handle: {
            breadcrumb: "Order editing",
            permission: SettingsPermissions.SET_GENERAL_SETTINGS,
          },
          path: "order-editing",
          lazy: async () => {
            const { OrderEditingSettingsPage } = await import(
              "../../../setting/order-editing/order-editing-settings"
            );
            return { Component: OrderEditingSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Routing rules",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "routing-rules",
          lazy: async () => {
            const { RoutingRulesSettingsPage } = await import(
              "@redotech/redo-merchant-fulfillment-app/setting/routing-rules"
            );
            return { Component: RoutingRulesSettingsPage };
          },
        },
      ],
    },
    {
      handle: {
        breadcrumb: "Order management",
        breadcrumbNavigate: false,
        permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
      },
      path: "oms",
      children: [
        {
          handle: {
            breadcrumb: "General",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "general",
          lazy: async () => {
            const { GeneralOmsSettingsPage } = await import(
              "@redotech/redo-merchant-fulfillment-app/setting/general"
            );
            return { Component: GeneralOmsSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Security",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "security",
          lazy: async () => {
            const { SecuritySettingsPage } = await import(
              "@redotech/redo-merchant-fulfillment-app/setting/security"
            );
            return { Component: SecuritySettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Scanning & warehouse",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "scanning-warehouse",
          lazy: async () => {
            const { ScanningWarehouseSettingsPage } = await import(
              "@redotech/redo-merchant-fulfillment-app/setting/scanning-warehouse"
            );
            return { Component: ScanningWarehouseSettingsPage };
          },
        },

        {
          handle: {
            breadcrumb: "Auto-merge shipments",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "automerge-shipments",
          lazy: async () => {
            const { AutoMergeSettingsPage } = await import(
              "@redotech/redo-merchant-fulfillment-app/setting/auto-merge-shipments"
            );
            return { Component: AutoMergeSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Automations",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "automations",
          children: [
            {
              index: true,
              lazy: async () => {
                const { AutomationsPage: Automations } = await import(
                  "@redotech/redo-merchant-fulfillment-app/setting/automations/automations"
                );
                return { Component: Automations };
              },
            },
            {
              handle: {
                breadcrumb: "Automation Builder",
                permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
              },
              path: ":automationId",

              async lazy() {
                const { OmsAutomationBuilder } = await import(
                  "../../../setting/oms/automations/automation-builder"
                );
                return { Component: OmsAutomationBuilder };
              },
            },
          ],
        },
        {
          handle: {
            breadcrumb: "Work Order Flows",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "work-order-flows",
          children: [
            {
              index: true,
              lazy: async () => {
                const { WorkOrderFlowsPage } = await import(
                  "@redotech/redo-merchant-fulfillment-app/setting/work-order-flows/work-order-flows"
                );
                return { Component: WorkOrderFlowsPage };
              },
            },
            {
              handle: {
                breadcrumb: "Flow Builder",
                permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
              },
              path: ":flowId",
              async lazy() {
                const { WorkOrderFlowBuilder } = await import(
                  "../../../setting/oms/work-order-flows/work-order-flow-builder"
                );
                return { Component: WorkOrderFlowBuilder };
              },
            },
          ],
        },
        {
          path: "balance",
          element: <Navigate replace to="../../billing/outbound" />,
        },
        {
          handle: {
            breadcrumb: "Integrations",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "integrations",
          lazy: async () => {
            const { IntegrationsPage } = await import(
              "@redotech/redo-merchant-fulfillment-app/setting/integrations"
            );
            return { Component: IntegrationsPage };
          },
        },
        {
          handle: {
            breadcrumb: "International",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "international",
          lazy: async () => {
            const { LoadInternationalSettingsPage } = await import(
              "@redotech/redo-merchant-fulfillment-app/setting/international"
            );
            return { Component: LoadInternationalSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Packages",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "packages",
          lazy: async () => {
            const { ParcelsPage: Parcels } = await import(
              "@redotech/redo-merchant-fulfillment-app/setting/parcels"
            );
            return { Component: Parcels };
          },
        },
        {
          handle: {
            breadcrumb: "Presets",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "presets",
          lazy: async () => {
            const { PresetsPage } = await import(
              "@redotech/redo-merchant-fulfillment-app/setting/presets/presets"
            );
            return { Component: PresetsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Reports",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "reports",
          lazy: async () => {
            const { ReportsPage } = await import(
              "@redotech/redo-merchant-fulfillment-app/setting/reports/oms-reports-page"
            );
            return { Component: ReportsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Warehouse builder",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "warehouse-builder",
          lazy: async () => {
            const { WarehouseBuilderWithProvider } = await import(
              "@redotech/redo-merchant-fulfillment-app/wms/warehouse-builder/warehouse-builder"
            );
            return { Component: WarehouseBuilderWithProvider };
          },
        },
        {
          handle: {
            breadcrumb: "Warehouse layouts",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "warehouse-layouts",
          lazy: async () => {
            const { WarehouseLayoutsPage } = await import(
              "@redotech/redo-merchant-fulfillment-app/setting/warehouse-layouts"
            );
            return { Component: WarehouseLayoutsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Custom imports",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "custom-import-schemas",
          lazy: async () => {
            const { CustomImportSchemas } = await import(
              "@redotech/redo-merchant-fulfillment-app/setting/custom-import-schemas"
            );
            return { Component: CustomImportSchemas };
          },
        },
      ],
    },
    { path: "parcels", element: <Navigate replace to="../oms/packages" /> },
    { path: "balance", element: <Navigate replace to="../oms/balance" /> },
    // We moved /locations -> /shipping, so we'll redirect users just in case.
    { path: "locations", element: <Navigate replace to="../shipping" /> },
  ],

  [SettingsSectionEnum.SHIPPING]: [
    {
      handle: {
        breadcrumb: "Carriers",
        permission: SettingsPermissions.SET_SHIPPING_SETTINGS,
      },
      path: "carriers",
      lazy: async () => {
        const { CarriersPage: Carriers } = await import(
          "@redotech/redo-merchant-fulfillment-app/setting/carrier-accounts/carriers-page"
        );
        return { Component: Carriers };
      },
    },
    {
      handle: {
        breadcrumb: "Fulfillment promise",
        permission: SettingsPermissions.SET_SHIPPING_SETTINGS,
      },
      path: "fulfillment-cutoff",
      lazy: async () => {
        const { FulfillmentCutoffPage } = await import(
          "../../../setting/fulfillment-cutoff/fulfillment-cutoff-page"
        );
        return { Component: FulfillmentCutoffPage };
      },
    },
    {
      handle: {
        breadcrumb: "Freight",
        permission: SettingsPermissions.SET_SHIPPING_SETTINGS,
      },
      path: "freight",
      lazy: async () => {
        const { FreightPage } = await import(
          "../../../setting/freight/freight-page"
        );
        return { Component: FreightPage };
      },
    },
  ],

  [SettingsSectionEnum.SUPPORT]: [
    {
      handle: {
        breadcrumb: "General",
        permission: SettingsPermissions.SET_SUPPORT_SETTINGS,
      },
      path: "general",
      children: [
        {
          index: true,
          lazy: async () => {
            const { SupportGeneral } = await import(
              "@redotech/redo-merchant-support-app/setting/general"
            );
            return { element: <SupportGeneral /> };
          },
        },
        {
          handle: { breadcrumb: "Contact Form" },
          path: "contact-form",
          lazy: async () => {
            const { ContactFormSettingsNew } = await import(
              "@redotech/redo-merchant-support-app/setting/general/contact-form-settings-new"
            );
            return { Component: ContactFormSettingsNew };
          },
        },
      ],
    },
    {
      handle: {
        breadcrumb: "Integrations",
        permission: SettingsPermissions.SET_SUPPORT_SETTINGS,
      },
      path: "channels",
      children: [
        {
          path: "",
          lazy: async () => {
            const { ChannelsPage } = await import("../../../setting/channels");
            return { Component: ChannelsPage };
          },
        },
        {
          handle: { breadcrumb: "Support Email" },
          path: "supportEmail",
          lazy: async () => {
            const { SupportEmailInfo } = await import(
              "@redotech/redo-merchant-support-app/setting/email-info"
            );
            return { Component: SupportEmailInfo };
          },
        },
        {
          handle: { breadcrumb: "Tiktok" },
          path: "tiktok-callback",
          async lazy() {
            const { Callback } = await import(
              "@redotech/redo-merchant-support-app/tiktok-callback"
            );
            return { element: <Callback /> };
          },
        },
        {
          handle: { breadcrumb: "TikTok Shop" },
          path: "tiktok-settings",
          async lazy() {
            const { TiktokSettings } = await import(
              "../../../setting/channel/tiktok-settings"
            );
            return { element: <TiktokSettings /> };
          },
        },
        {
          handle: { breadcrumb: "Meta" },
          path: "meta-connect",
          async lazy() {
            const { Connect } = await import(
              "@redotech/redo-merchant-support-app/meta/connect"
            );
            return { element: <Connect /> };
          },
        },
        {
          handle: { breadcrumb: "Meta" },
          path: "meta-settings",
          async lazy() {
            const { MetaSettings } = await import(
              "../../../setting/channel/meta-settings"
            );
            return { element: <MetaSettings /> };
          },
        },
        {
          handle: { breadcrumb: "Voice" },
          path: "voice-settings",
          async lazy() {
            const { VoiceAndSmsSettings } = await import(
              "../../../setting/channel/voice-and-sms/voice-and-sms-settings"
            );
            return { element: <VoiceAndSmsSettings /> };
          },
        },
        {
          handle: { breadcrumb: "Phone" },
          path: "phone-setup",
          async lazy() {
            const { PhoneSetup } = await import(
              "@redotech/redo-merchant-support-app/phone/setup"
            );
            return { element: <PhoneSetup /> };
          },
        },
        {
          handle: { breadcrumb: "Email" },
          path: "email-connect",
          async lazy() {
            const { EmailSetup } = await import("../../../setting/email-setup");
            return { element: <EmailSetup /> };
          },
        },
        {
          path: "meta-callback",
          async lazy() {
            const { Callback } = await import(
              "@redotech/redo-merchant-support-app/meta/meta-callback"
            );
            return { element: <Callback /> };
          },
        },
        {
          handle: { breadcrumb: "Attentive" },
          path: "attentive-connect",
          async lazy() {
            const { Connect } = await import(
              "@redotech/redo-merchant-support-app/attentive/connect"
            );
            return { element: <Connect /> };
          },
        },
        {
          handle: { breadcrumb: "Postscript" },
          path: "postscript-connect",
          async lazy() {
            const { Connect } = await import(
              "@redotech/redo-merchant-support-app/postscript/connect"
            );
            return { element: <Connect /> };
          },
        },
        {
          path: "attentive-callback",
          async lazy() {
            const { Callback } = await import(
              "@redotech/redo-merchant-support-app/attentive-callback"
            );
            return { element: <Callback /> };
          },
        },
        {
          path: "gmail-callback",
          async lazy() {
            const { Callback } = await import(
              "@redotech/redo-merchant-support-app/gmail-callback"
            );
            return { element: <Callback /> };
          },
        },
        {
          path: "outlook-callback",
          async lazy() {
            const { Callback } = await import(
              "@redotech/redo-merchant-support-app/outlook-callback"
            );
            return { element: <Callback /> };
          },
        },
      ],
    },
    {
      handle: {
        breadcrumb: "CSAT",
        permission: SettingsPermissions.SET_SUPPORT_SETTINGS,
      },
      path: "customer-satisfaction",
      lazy: async () => {
        const { CustomerSatisfaction } = await import(
          "../../../setting/customer-satisfaction"
        );
        return { Component: CustomerSatisfaction };
      },
    },
    {
      handle: {
        breadcrumb: "Tags",
        permission: SettingsPermissions.SET_SUPPORT_SETTINGS,
      },
      path: "tags",
      lazy: async () => {
        const { Tags } = await import(
          "../../../setting/conversation-tags/tags"
        );
        return { Component: Tags };
      },
    },
    {
      handle: {
        breadcrumb: "FAQ",
        permission: SettingsPermissions.SET_SUPPORT_SETTINGS,
      },
      path: "faq",
      element: <FaqRedirect />,
    },
    {
      handle: {
        breadcrumb: "Rules",
        permission: SettingsPermissions.SET_SUPPORT_SETTINGS,
      },
      path: "rules",
      children: [
        {
          path: "",
          lazy: async () => {
            const { RulesListRouter } = await import(
              "../../../setting/rules/rules-router"
            );
            return { element: <RulesListRouter /> };
          },
        },
        {
          handle: { breadcrumb: "New rule" },
          path: "new",
          lazy: async () => {
            const { CreateRuleRouter } = await import(
              "../../../setting/rules/rules-router"
            );
            return { element: <CreateRuleRouter /> };
          },
        },
        {
          handle: { breadcrumb: "Edit rule" },
          path: "edit/:ruleId",
          lazy: async () => {
            const { CreateRuleRouter } = await import(
              "../../../setting/rules/rules-router"
            );
            return { element: <CreateRuleRouter /> };
          },
        },
        {
          handle: { breadcrumb: "Edit rule" },
          path: "edit-v2/:ruleId",
          lazy: async () => {
            const { CreateRuleRouter } = await import(
              "../../../setting/rules/rules-router"
            );
            return { element: <CreateRuleRouter /> };
          },
        },
        {
          handle: { breadcrumb: "Edit rule" },
          path: "edit-v2/",
          lazy: async () => {
            const { CreateRuleRouter } = await import(
              "../../../setting/rules/rules-router"
            );
            return { element: <CreateRuleRouter /> };
          },
        },
      ],
    },
  ],
};

const routeChildren = Object.values(settingsRoutesBySection).reduce<
  PermissionedRouteObject[]
>((acc, section) => {
  if (Array.isArray(section)) {
    return [...acc, ...section];
  } else {
    return [...acc, section];
  }
}, [] as PermissionedRouteObject[]);

// Add redirects for old routes
routeChildren.push(
  // Redirect old try-first routes to conversion/try-first
  {
    path: "try-first",
    element: <Navigate replace to="../conversion/try-first" />,
  },
  {
    path: "try-first/*",
    element: <Navigate replace to="../conversion/try-first" />,
  },

  // Redirect old checkout-optimization routes to conversion/delivery-estimate
  {
    path: "checkout-optimization",
    element: <Navigate replace to="../conversion/delivery-estimate" />,
  },
  {
    path: "checkout-optimization/*",
    element: <Navigate replace to="../conversion/delivery-estimate" />,
  },
);

export const settingRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Settings", breadcrumbNavigate: false },
    lazy: async () => {
      const { Page } = await import("../../page");
      return {
        element: (
          <Page
            hideHeader={(url) =>
              fullscreen(url) ||
              inForge(url) ||
              inPhoneSetup(url) ||
              inNewFlowEditor(url) ||
              inLocationEditor(url) ||
              inLabelsEditor(url)
            }
            hideNavbar={(url) =>
              fullscreen(url) ||
              inForge(url) ||
              inPhoneSetup(url) ||
              inNewFlowEditor(url) ||
              inLocationEditor(url) ||
              inLabelsEditor(url)
            }
            hidePadding={hidePadding}
          />
        ),
      };
    },
    children: routeChildren,
  },
];
